import { computed, effect, ReadableAtom, task } from 'nanostores';
import { storedAtom } from '../atoms/storedAtom';
import { delay, uuid } from '@razz-team/utils';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { reportSessionId } from '../utils/tracking';
import { createApiClient } from './createApiClient';
import { ApiMemberRole, ApiToken } from './index';

function getOrCreateBrowserSessionId() {
  const existing = localStorage.getItem('sessionId');
  if (!existing) {
    const created = uuid();
    localStorage.setItem('sessionId', created);
    return created;
  }

  return existing;
}

export type AuthState = 'loading' | 'authenticated' | 'unauthenticated';

export function createBrowserApiClient({ baseUrl }: { baseUrl: string }) {
  const sessionId = getOrCreateBrowserSessionId();
  reportSessionId(sessionId);

  const $accessToken = storedAtom('access_token');
  const $refreshToken = storedAtom('refresh_token');
  const $businessId = storedAtom('business_id');

  function setRefreshToken(refreshToken: string | null) {
    $refreshToken.set(refreshToken);
  }

  function setAccessToken(accessToken: string | null) {
    $accessToken.set(accessToken);
  }

  async function setBusinessId(businessId: string | null) {
    $businessId.set(businessId);
    return refreshToken();
  }

  async function refreshTokenUntilOk(body: { refreshToken: string; businessId: string | null }) {
    const result = await client.POST('/auth/token', {
      body,
      headers: { 'Idempotency-Key': createIdempotencyKey() },
    });

    if (result.response.ok) {
      return result.data;
    }

    if (result.response.status >= 400 && result.response.status < 500) {
      return undefined;
    }

    // some server error, retry
    await delay(5000 * Math.random());
    return refreshTokenUntilOk(body);
  }

  async function refreshToken() {
    const abort = new AbortController();
    await navigator.locks.request('refresh_token', { signal: abort.signal }, async () => {
      let res: ApiToken | undefined;
      const refreshToken = $refreshToken.get();
      if (refreshToken) {
        const businessId = $businessId.get();
        res = await refreshTokenUntilOk({ refreshToken, businessId });
      }

      setAccessToken(res?.accessToken ?? null);
      setRefreshToken(res?.refreshToken ?? null);
    });

    return $accessToken.get() !== null;
  }

  const client = createApiClient({
    baseUrl,
    refreshToken,
    getSessionId() {
      return sessionId;
    },
    getAccessToken() {
      return $accessToken.get();
    },
  });

  const $authState = computed([$accessToken], (accessToken) =>
    task(async () => {
      if (accessToken) {
        return 'authenticated';
      }

      try {
        if (await refreshToken()) {
          return 'authenticated';
        }
      } catch (error) {
        console.error(error);
      }

      return 'unauthenticated';
    }),
  );

  const $role = computed([$accessToken], (accessToken) => {
    if (accessToken) {
      try {
        const { role } = JSON.parse(atob(accessToken.split('.')[1]));
        if (typeof role === 'string') {
          return role as ApiMemberRole;
        }
      } catch (error) {
        console.error(error);
      }
    }

    return 'MEMBER' as ApiMemberRole;
  });

  effect([$businessId, $role], (businessId, role) =>
    console.log(`business: ${businessId}, role: ${role}`),
  );

  return {
    client,
    $role,
    $authState,
    $businessId: $businessId as ReadableAtom<string | null>,
    setBusinessId,
    setAccessToken,
    setRefreshToken,
  };
}
